import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, RefreshCw, Wifi, WifiOff, Activity, Globe, Cpu, Router, Server, Camera, Printer, Cable, ShieldAlert, ShieldCheck, Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { EndpointEditModal } from '@/components/EndpointEditModal';

interface MonitoredEndpoint {
  id: number;
  tenant_id: number;
  customer_id: number | null;
  customer?: { id: number; name: string; external_id: string | null } | null;
  name: string;
  description: string | null;
  site: string | null;
  kind: string;
  ip_address: string;
  mac_address: string | null;
  probe_method: string;
  tcp_port: number | null;
  poll_interval_sec: number;
  enabled: boolean;
  silent: boolean;
  auto_discovered: boolean;
  last_probe_at: string | null;
  last_status: 'reachable' | 'unreachable' | 'unknown';
  last_latency_ms: number | null;
  last_method_used: string | null;
  consecutive_failures: number;
  first_failed_at: string | null;
  last_seen_at: string | null;
  ssl_expired: boolean | null;
  ssl_expires_in_days: number | null;
  ssl_subject_cn: string | null;
  metadata: Record<string, unknown>;
}

// Taxonomie : kind (macro) + metadata.location + metadata.type
// Tabs reflètent les axes les plus utiles : où ça se trouve / à qui ça appartient.
type Tab = { key: string; label: string; filter: (e: { kind: string; metadata: Record<string, unknown> }) => boolean };
const TABS: Tab[] = [
  { key: 'all',         label: 'Tous',                filter: () => true },
  { key: 'internet',    label: 'Internet Health',     filter: (e) => (e.metadata?.location === 'internet') || e.kind === 'internet_health' },
  { key: 'infra_int',   label: 'Infra interne',       filter: (e) => e.kind === 'infra' && e.metadata?.location === 'internal' },
  { key: 'infra_ext',   label: 'Infra externe',       filter: (e) => e.kind === 'infra' && e.metadata?.location === 'external' },
  { key: 'customer',    label: 'Équipements client',  filter: (e) => e.kind === 'customer' },
  { key: 'discovered',  label: 'Auto-discovered',     filter: (e) => e.kind === 'discovered_unmanaged' },
];

const KIND_ICONS: Record<string, any> = {
  cpe_no_tr069: Cpu, router: Router, switch: Cable, ap: Wifi, camera: Camera, server: Server,
  printer: Printer, internet_health: Globe, discovered_unmanaged: Cpu, other: Activity,
};

export default function Endpoints() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const [tab, setTab] = useState(params.get('tab') ?? 'all');
  const [createOpen, setCreateOpen] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (q) next.set('q', q); else next.delete('q');
      if (tab !== 'all') next.set('tab', tab); else next.delete('tab');
      setParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, tab]);

  const { data, isLoading } = useQuery({
    queryKey: ['endpoints', q, tab],
    queryFn: async () => {
      // Une seule query — on filtre client-side selon le tab (rare > qq centaines de rows)
      const r = await api.get<{ data: MonitoredEndpoint[]; total: number }>('/endpoints', { params: { q: q || undefined, per_page: 500 } });
      const tabDef = TABS.find((t) => t.key === tab) ?? TABS[0];
      const filtered = r.data.data.filter((e) => tabDef.filter(e as any));
      return { data: filtered.sort((a, b) => a.name.localeCompare(b.name)), total: filtered.length };
    },
    refetchInterval: 30000,
  });

  const probeMut = useMutation({
    mutationFn: (id: number) => api.post(`/endpoints/${id}/probe`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['endpoints'] }),
  });

  const rows = data?.data ?? [];
  const counts = {
    reachable: rows.filter((r) => r.last_status === 'reachable').length,
    unreachable: rows.filter((r) => r.last_status === 'unreachable').length,
    unknown: rows.filter((r) => r.last_status === 'unknown').length,
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <PageHeader
        title="Équipements monitorés"
        subtitle={`${data?.total ?? 0} endpoints · ${counts.reachable} ✓ · ${counts.unreachable} ✗ · ${counts.unknown} ?`}
        actions={
          <button className="btn btn-primary btn-sm" onClick={() => setCreateOpen(true)}>
            <Plus size={11} />Nouvel endpoint
          </button>
        }
      />

      <EndpointEditModal open={createOpen} onClose={() => setCreateOpen(false)} editing={null} />

      {/* Tabs */}
      <div className="px-4 pt-2 border-b border-border bg-bg-2 flex items-center gap-1 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`h-7 px-3 text-xs rounded-t border-b-2 ${tab === t.key ? 'border-brand text-brand bg-bg' : 'border-transparent text-text-muted hover:text-text'}`}
          >
            {t.label}
          </button>
        ))}
        <div className="ml-auto relative w-72 mb-1">
          <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-faint" />
          <input
            className="input pl-7 w-full h-7 text-xs"
            placeholder="Recherche nom, IP, description…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="luc-table">
          <thead className="sticky top-0 bg-bg z-10">
            <tr>
              <th>Status</th>
              <th>Type</th>
              <th>Nom</th>
              <th>IP / MAC</th>
              <th>Customer</th>
              <th>Last probe</th>
              <th>Latency</th>
              <th>SSL</th>
              <th>Échecs</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => {
              const Icon = KIND_ICONS[e.kind] ?? Activity;
              const statusColor = e.last_status === 'reachable' ? 'text-success' : e.last_status === 'unreachable' ? 'text-danger' : 'text-text-faint';
              return (
                <tr key={e.id} className="cursor-pointer hover:bg-bg-2">
                  <td>
                    <span className={`flex items-center gap-1 ${statusColor}`}>
                      {e.last_status === 'reachable' ? <Wifi size={11} /> : e.last_status === 'unreachable' ? <WifiOff size={11} /> : <Activity size={11} />}
                      <span className="text-xs">{e.last_status}</span>
                    </span>
                  </td>
                  <td>
                    <span className="flex items-center gap-1 text-[11px] text-text-muted">
                      <Icon size={10} />
                      <span className="font-mono">{e.kind.replace(/_/g, ' ')}</span>
                    </span>
                  </td>
                  <td>
                    <Link to={`/app/endpoints/${e.id}`} className="hover:text-brand text-xs">{e.name}</Link>
                    {e.auto_discovered && <span className="badge text-[9px] ml-1.5 text-text-faint">auto</span>}
                    {e.silent && <span className="badge text-[9px] ml-1 text-text-faint" title="Pas d'alerte sur transitions">silent</span>}
                  </td>
                  <td className="font-mono text-[11px] text-text-muted">
                    <div>{e.ip_address}</div>
                    {e.mac_address && <div className="text-[10px] text-text-faint">{e.mac_address}</div>}
                  </td>
                  <td className="text-xs">
                    {e.customer ? <Link to={`/app/customers/${e.customer.id}`} className="hover:text-brand">{e.customer.name}</Link> : <span className="text-text-faint italic">—</span>}
                  </td>
                  <td className="font-mono text-[11px] text-text-faint">
                    {e.last_probe_at ? new Date(e.last_probe_at).toLocaleString('fr', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </td>
                  <td className="font-mono text-[11px]">
                    {e.last_latency_ms !== null ? <span className="text-brand">{e.last_latency_ms}ms</span> : '—'}
                    {e.last_method_used && <span className="ml-1 text-[9px] text-text-faint">{e.last_method_used}</span>}
                  </td>
                  <td>
                    {e.ssl_expired ? (
                      <span className="flex items-center gap-1 text-danger text-[11px]" title={`Expiré ${e.ssl_subject_cn ?? ''}`}>
                        <ShieldAlert size={10} />EXPIRED
                      </span>
                    ) : e.ssl_expires_in_days !== null ? (
                      <span className={`flex items-center gap-1 text-[11px] ${e.ssl_expires_in_days < 10 ? 'text-danger' : e.ssl_expires_in_days < 30 ? 'text-warning' : 'text-success'}`}
                            title={e.ssl_subject_cn ?? ''}>
                        <ShieldCheck size={10} />{e.ssl_expires_in_days}j
                      </span>
                    ) : (
                      <span className="text-text-faint text-[11px]">—</span>
                    )}
                  </td>
                  <td className="font-mono text-[11px]">
                    {e.consecutive_failures > 0 ? <span className="text-warning">{e.consecutive_failures}</span> : <span className="text-text-faint">0</span>}
                  </td>
                  <td>
                    <button className="btn btn-sm" title="Probe maintenant"
                            disabled={probeMut.isPending}
                            onClick={(ev) => { ev.preventDefault(); probeMut.mutate(e.id); }}>
                      <RefreshCw size={10} className={probeMut.isPending ? 'animate-spin' : ''} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={10} className="text-center py-12 text-text-faint italic">Aucun endpoint dans cette catégorie.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
